import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { PurchaseOrderReceiptDispositionStatus } from '../../enums/purchase-order-receipt-disposition-status.enum';
import { BaseEntity } from './base.entity';
import { CompanyEntity } from './company.entity';
import { PurchaseOrderReceiptDetailEntity } from './purchase-order-receipt-detail.entity';
import { VendorEntity } from './vendor.entity';
import { WarehouseEntity } from './warehouse.entity';

@Index('externalId_fk_company__id', ['externalId', 'fkCompanyId'], {
	unique: true,
})
@Index('fk_company__id', ['fkCompanyId'], {})
@Index('purchase_order_receipt__fk_vendor__id_fk', ['fkVendorId'], {})
@Index('purchase_order_receipt__fk_warehouse__id_fk', ['fkWarehouseId'], {})
@Index(
	'purchase_order_receipt__packingSlipNumber_idx',
	['packingSlipNumber'],
	{},
)
@Entity('purchase_order_receipt')
export class PurchaseOrderReceiptEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', nullable: true, length: 64 })
	public externalId!: string | null;

	@Column('datetime', { name: 'receiptDate' })
	public receiptDate!: Date;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('int', {
		name: 'fk_company__id',
		unsigned: true,
		default: () => "'1'",
	})
	public fkCompanyId!: number;

	/**
	 * Receiving vendor. Denormalized for query speed (every Receipts
	 * tab filter joins on vendor); validated on save to match the
	 * vendor of all linked PurchaseOrderDetails. NULL only on
	 * historical rows pre-dating this column.
	 */
	@Column('int', { name: 'fk_vendor__id', unsigned: true, nullable: true })
	public fkVendorId!: number | null;

	@ManyToOne(() => VendorEntity, { onDelete: 'RESTRICT', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_vendor__id', referencedColumnName: 'id' }])
	public fkVendor!: VendorEntity | null;

	/**
	 * Receiving warehouse — one truck = one warehouse. NULL only on
	 * historical rows pre-dating this column.
	 */
	@Column('int', { name: 'fk_warehouse__id', unsigned: true, nullable: true })
	public fkWarehouseId!: number | null;

	@ManyToOne(() => WarehouseEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_warehouse__id', referencedColumnName: 'id' }])
	public fkWarehouse!: WarehouseEntity | null;

	/**
	 * When the truck pulled into the dock — distinct from the row's
	 * `_created_date` (when the clerk processed the receipt) and from
	 * `receiptDate` (the business-effective date on the receipt
	 * document). Drives the time-to-acknowledge widget (R3).
	 */
	@Column('datetime', { name: 'dockArrivalAt', nullable: true })
	public dockArrivalAt!: Date | null;

	/** UPS / FedEx / vendor-truck name. For carrier damage claims. */
	@Column('varchar', { name: 'carrierName', nullable: true, length: 64 })
	public carrierName!: string | null;

	/** Carrier PRO / tracking number. */
	@Column('varchar', { name: 'proNumber', nullable: true, length: 64 })
	public proNumber!: string | null;

	/** Vendor's packing slip ref. Indexed because the receive wizard searches by it. */
	@Column('varchar', { name: 'packingSlipNumber', nullable: true, length: 64 })
	public packingSlipNumber!: string | null;

	@Column('text', { name: 'notes', nullable: true })
	public notes!: string | null;

	/**
	 * Derived rollup over child detail dispositionStatuses. Recomputed
	 * by the service layer on any exception mutation. See
	 * `libs/core/src/utils/purchaseOrderReceiptStatus.ts`.
	 */
	@Column('varchar', {
		name: 'dispositionStatus',
		length: 32,
		default: () => `'${PurchaseOrderReceiptDispositionStatus.CLEAN}'`,
	})
	public dispositionStatus!: PurchaseOrderReceiptDispositionStatus;

	@ManyToOne(() => CompanyEntity, (company) => company.purchaseOrderReceipts, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@OneToMany(
		() => PurchaseOrderReceiptDetailEntity,
		(purchaseOrderReceiptDetail) =>
			purchaseOrderReceiptDetail.fkPurchaseOrderReceipt,
		{},
	)
	public purchaseOrderReceiptDetails!: Array<PurchaseOrderReceiptDetailEntity>;

	// Exceptions and file attachments wired in Phase 1.3 once those
	// entities land — see docs/claude/po-receipts/PLAN.md.
}
